import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
// import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Shield, Lock, AlertTriangle } from 'lucide-react'
import { userManagementService } from '@/services/user-management-service'
import { roleManagementService } from '@/services/role-management-service'
import { authService, type User } from '@/services/auth-service'

type ExtendedUser = User

interface RoleBasedAppProps {
  appName: string
  appDisplayName: string
  appDescription?: string
  children: React.ReactNode
  requiredRole?: 'admin' | 'user'
  allowSupervisor?: boolean
  fallbackComponent?: React.ReactNode
  loadingFallback?: React.ReactNode
}

export function RoleBasedApp({
  appName,
  appDisplayName,
  appDescription,
  children,
  requiredRole,
  allowSupervisor,
  fallbackComponent,
  loadingFallback,
}: RoleBasedAppProps) {
  const [user, setUser] = useState<ExtendedUser | null>(null)
  const [hasAccess, setHasAccess] = useState<boolean | null>(null)
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    const evaluateAccess = async () => {
      try {
        const currentUser = authService.getCachedUser()
        if (!isMounted) return
        if (!currentUser) {
          setHasAccess(false)
          return
        }

        setUser(currentUser)

        if (requiredRole === 'admin' && currentUser.role !== 'admin') {
          // Check if supervisor access is allowed for this app
          if (allowSupervisor) {
            try {
              const status = await roleManagementService.getSupervisorStatus()
              if (status.is_supervisor) {
                setHasAccess(true)
                return
              }
            } catch {
              // Not a supervisor
            }
          }
          setHasAccess(false)
          return
        }

        const { isSystemApp } = await import('@/data/apps')
        if (isSystemApp(appName)) {
          setHasAccess(true)
          return
        }

        const canAccess = await userManagementService.canUserAccessApp(currentUser.id, appName)
        setHasAccess(canAccess)
      } catch (error) {
        console.error('Failed to evaluate role based access', error)
        if (isMounted) {
          setHasAccess(false)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    evaluateAccess()

    return () => {
      isMounted = false
    }
  }, [appName, requiredRole])

  if (isLoading) {
    if (loadingFallback) {
      return <>{loadingFallback}</>
    }
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>{t('roleBasedApp.checkingAccessHelper', { defaultValue: 'Checking permissions...' })}</p>
        </div>
      </div>
    )
  }

  if (!hasAccess) {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>
    }

    return (
      <div className="h-full flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/20">
                <Lock className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <CardTitle className="text-xl">{appDisplayName}</CardTitle>
            <CardDescription>{appDescription || t('roleBasedApp.accessTo', { app: appDisplayName })}</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">{t('roleBasedApp.accessDenied')}</span>
              </div>
              <p className="text-sm text-[var(--text-secondary)]">
                {t('roleBasedApp.noPermission')}
              </p>
            </div>

            {user && (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                    {user.role === 'admin' ? (
                      <>
                        <Shield className="h-3 w-3 mr-1" />
                        {t('roleBasedApp.roleAdmin')}
                      </>
                    ) : (
                      t('roleBasedApp.roleUser')
                    )}
                  </Badge>
                  <Badge variant={user.isActive ? 'default' : 'secondary'}>
                    {user.isActive ? t('roleBasedApp.active') : t('roleBasedApp.inactive')}
                  </Badge>
                </div>
                <p className="text-xs text-[var(--text-secondary)]">{t('roleBasedApp.user', { email: user.email })}</p>
              </div>
            )}

            <div className="pt-4 border-t">
              <p className="text-xs text-[var(--text-secondary)]">
                {t('roleBasedApp.contactAdmin')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}

export function AdminOnlyApp({
  appName,
  appDisplayName,
  appDescription,
  children,
  allowSupervisor,
  loadingFallback,
}: Omit<RoleBasedAppProps, 'requiredRole'>) {
  return (
    <RoleBasedApp
      appName={appName}
      appDisplayName={appDisplayName}
      appDescription={appDescription}
      requiredRole="admin"
      allowSupervisor={allowSupervisor}
      loadingFallback={loadingFallback}
    >
      {children}
    </RoleBasedApp>
  )
}

export function CustomAccessControl({
  appName,
  appDisplayName,
  appDescription,
  children,
  fallbackComponent,
  loadingFallback,
}: RoleBasedAppProps) {
  return (
    <RoleBasedApp
      appName={appName}
      appDisplayName={appDisplayName}
      appDescription={appDescription}
      fallbackComponent={fallbackComponent}
      loadingFallback={loadingFallback}
    >
      {children}
    </RoleBasedApp>
  )
}
